import {
  InternalAuditExecution,
  InternalAuditExecutionItem,
  InternalAuditProcedure,
  InternalAuditProcedureSuggestion,
  InternalAuditScopeAssessment,
  InternalAuditTaskExecution,
  InternalAuditTaskExecutionItem,
} from 'models/internalAuditExecution';
import InternalAuditExecutionDTO, {
  InternalAuditExecutionItemDTO,
  InternalAuditProcedureDTO,
  InternalAuditProcedureSuggestionDTO,
  InternalAuditScopeAssessmentDTO,
  InternalAuditTaskExecutionDTO,
  InternalAuditTaskExecutionItemDTO,
} from 'models/dto/internalAuditExecutionDTO';
import { fromApiDateOnly, fromApiDateTimeOptional } from 'utils/datetime';

function mapFromInternalAuditExecutionItem(dto: InternalAuditExecutionItemDTO): InternalAuditExecutionItem {
  const item = new InternalAuditExecutionItem();
  item.entityType = dto.entityType;
  item.entityId = dto.entityId;
  item.code = dto.code;
  item.name = dto.name;
  item.outcome = dto.outcome;
  item.conclusion = dto.conclusion;
  item.assessedState = dto.assessedState;
  item.writeBackState = dto.writeBackState;
  item.assessedById = dto.assessedById;
  item.assessedOn = fromApiDateTimeOptional(dto.assessedOn);
  item.plannedOn = fromApiDateOnly(dto.plannedOn);
  item.taskId = dto.taskId;
  item.taskName = dto.taskName;
  item.procedureTaskId = dto.procedureTaskId ?? undefined;
  item.procedureName = dto.procedureName ?? undefined;
  item.taskStateId = dto.taskStateId;
  item.taskCompleted = dto.taskCompleted;
  item.assigneeId = dto.assigneeId;
  item.proposedTemplateTaskId = dto.proposedTemplateTaskId;
  item.proposedTemplateTaskName = dto.proposedTemplateTaskName;
  item.proposedUserId = dto.proposedUserId;
  item.proposalSource = dto.proposalSource;
  item.needsFinding = dto.needsFinding === true;

  return item;
}

function mapFromInternalAuditProcedure(dto: InternalAuditProcedureDTO): InternalAuditProcedure {
  const item = new InternalAuditProcedure();
  item.taskId = dto.taskId;
  item.name = dto.name;

  return item;
}

export function mapFromInternalAuditExecution(dto: InternalAuditExecutionDTO): InternalAuditExecution {
  const item = new InternalAuditExecution();
  item.internalAuditId = dto.internalAuditId;
  item.items = (dto.items ?? []).map(mapFromInternalAuditExecutionItem);
  item.procedures = (dto.procedures ?? []).map(mapFromInternalAuditProcedure);

  return item;
}

export function mapFromInternalAuditScopeAssessment(dto: InternalAuditScopeAssessmentDTO): InternalAuditScopeAssessment {
  const item = new InternalAuditScopeAssessment();
  item.internalAuditId = dto.internalAuditId;
  item.auditName = dto.auditName;
  item.auditStartDate = fromApiDateTimeOptional(dto.auditStartDate);
  item.auditEndDate = fromApiDateTimeOptional(dto.auditEndDate);
  item.entityType = dto.entityType;
  item.entityId = dto.entityId;
  item.outcome = dto.outcome;
  item.conclusion = dto.conclusion;
  item.assessedState = dto.assessedState;
  item.writeBackState = dto.writeBackState;
  item.assessedById = dto.assessedById;
  item.assessedOn = fromApiDateTimeOptional(dto.assessedOn);

  return item;
}

function mapFromInternalAuditTaskExecutionItem(dto: InternalAuditTaskExecutionItemDTO): InternalAuditTaskExecutionItem {
  const item = new InternalAuditTaskExecutionItem();
  item.entityType = dto.entityType;
  item.entityId = dto.entityId;
  item.code = dto.code;
  item.name = dto.name;
  item.outcome = dto.outcome;
  item.conclusion = dto.conclusion;
  item.assessedById = dto.assessedById;
  item.assessedOn = fromApiDateTimeOptional(dto.assessedOn);
  item.previous = dto.previous ? mapFromInternalAuditScopeAssessment(dto.previous) : undefined;

  return item;
}

export function mapFromInternalAuditTaskExecution(dto: InternalAuditTaskExecutionDTO): InternalAuditTaskExecution {
  const item = new InternalAuditTaskExecution();
  item.internalAuditId = dto.internalAuditId;
  item.auditName = dto.auditName;
  item.auditStatus = dto.auditStatus;
  item.canEdit = dto.canEdit ?? false;
  item.items = (dto.items ?? []).map(mapFromInternalAuditTaskExecutionItem);

  return item;
}

export function mapFromInternalAuditProcedureSuggestions(
  dtos: InternalAuditProcedureSuggestionDTO[] | undefined,
): InternalAuditProcedureSuggestion[] {
  return (dtos ?? []).map((dto) => {
    const item = new InternalAuditProcedureSuggestion();
    item.entityType = dto.entityType;
    item.entityId = dto.entityId;
    item.templateTaskId = dto.templateTaskId;
    item.reason = dto.reason;

    return item;
  });
}
